import React, { useEffect, useState } from 'react';
import { useSocket } from '../../context/SocketContext';
import { apiService } from '../../services/api';
import type { VotingResults } from '../../services/socketService';
import { BarChart3, CheckCircle2, Circle, Clock, Lock, MinusCircle } from 'lucide-react';

interface VotingResultsProps {
  votingSessionId?: string;
  className?: string;
}

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';

const VotingResultsComponent: React.FC<VotingResultsProps> = ({ 
  votingSessionId,
  className = ''
}) => {
  const { 
    activeVotingSession, 
    votingResults,
    isConnected 
  } = useSocket();
  
  const [animationKey, setAnimationKey] = useState(0);
  const [liveStats, setLiveStats] = useState<{
    totalVotes: number;
    participationRate: number;
    votedCoefficient: number;
    updatedAt: number;
  } | null>(null);

  // Animar cuando cambien los resultados (solo al finalizar)
  useEffect(() => {
    if (votingResults && activeVotingSession?.status === 'ended') {
      setAnimationKey(prev => prev + 1);
    }
  }, [votingResults, activeVotingSession]);

  const session = activeVotingSession;
  const results = votingResults;

  const applyLiveStats = (
    data: Partial<Pick<VotingResults, 'totalVotes' | 'participationRate' | 'votedCoefficient'>>
  ) => {
    setLiveStats((prev) => ({
      totalVotes: Number(data.totalVotes ?? prev?.totalVotes ?? 0),
      participationRate: Number(data.participationRate ?? prev?.participationRate ?? 0),
      votedCoefficient: Number(data.votedCoefficient ?? prev?.votedCoefficient ?? 0),
      updatedAt: Date.now(),
    }));
  };

  const formatCoefficient = (value?: number): string => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(numericValue);
  };

  // Si llegan métricas por socket durante sesión activa, se aplican como actualización inmediata.
  useEffect(() => {
    if (!session || session.status !== 'active' || !votingResults) {
      return;
    }

    applyLiveStats(
      {
        totalVotes: votingResults.totalVotes,
        participationRate: votingResults.participationRate,
        votedCoefficient: votingResults.votedCoefficient,
      }
    );
  }, [session?.id, session?.status, votingResults]);

  // Fuente base de consistencia para sesión activa: polling liviano periódico.
  useEffect(() => {
    if (!session || session.status !== 'active') {
      setLiveStats(null);
      return;
    }

    let isCancelled = false;

    const refreshLiveCounters = async () => {
      try {
        const response = await apiService.getVotingLiveStats(session.id);
        const data = response?.data?.data || response?.data;
        if (!data || isCancelled) return;

        applyLiveStats(
          {
            totalVotes: data.totalVotes,
            participationRate: data.participationRate,
            votedCoefficient: data.votedCoefficient,
          }
        );
      } catch (error) {
        // En error de polling, conservamos la última métrica conocida.
      }
    };

    refreshLiveCounters();
    const interval = setInterval(refreshLiveCounters, 8000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [session?.id, session?.status]);

  if (!session) {
    return (
      <div className={`${shellCardClassName} p-6 text-center ${className}`}>
        <svg className="mx-auto mb-3 h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <p className="text-slate-600">No hay votación activa</p>
      </div>
    );
  }

  const isSessionActive = session.status === 'active';
  const isSessionEnded = session.status === 'ended' || session.status === 'completed';

  const getOptionResult = (optionText: string) => {
    return results?.results.find(r => r.option === optionText);
  };

  const getOptionPercentage = (optionText: string): number => {
    const option = getOptionResult(optionText);
    if (!option) return 0;
    // Usar porcentaje ponderado por coeficiente
    return Math.round(option.percentageByCoefficient || option.percentage || 0);
  };

  const getWinnerOption = (): string | null => {
    if (!results || !results.results || results.results.length === 0) return null;
    if (results.winner) return results.winner;

    // Fallback: mayor porcentaje por coeficiente
    const sorted = [...results.results].sort((a, b) => (b.percentageByCoefficient || 0) - (a.percentageByCoefficient || 0));
    return sorted[0]?.option || null;
  };

  const winner = getWinnerOption();

  const OptionResult: React.FC<{ optionText: string; index: number }> = ({ optionText, index }) => {
    const optionData = getOptionResult(optionText);
    const votes = optionData?.votes || 0;
    const coefficient = optionData?.coefficient || 0;
    const percentage = getOptionPercentage(optionText);
    const isWinner = winner === optionText && isSessionEnded;
    
    return (
      <div 
        key={`${animationKey}-${optionText}`}
        className={`relative rounded-xl border p-4 transition-all duration-500 ${
          isWinner 
            ? 'border-green-500 bg-green-50' 
            : 'border-slate-200 bg-white hover:bg-slate-50'
        }`}
        style={{ animationDelay: `${index * 100}ms` }}
      >
        {/* Indicador de ganador */}
        {isWinner && (
          <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-2">
          <h4 className={`font-medium ${isWinner ? 'text-green-800' : 'text-slate-900'}`}>
            {optionText}
          </h4>
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 min-w-0">
            <span className={`text-sm ${isWinner ? 'text-green-700' : 'text-slate-600'}`}>
              {votes} votos
            </span>
            <span className={`text-xs sm:text-sm font-mono break-all ${isWinner ? 'text-green-700' : 'text-slate-600'}`}>
              Coef: {formatCoefficient(coefficient)}
            </span>
            <span className={`text-lg font-bold ${isWinner ? 'text-green-800' : 'text-slate-800'}`}>
              {percentage}%
            </span>
          </div>
        </div>
        
        {/* Barra de progreso */}
        <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full transition-all duration-1000 ease-out ${
              isWinner 
                ? 'bg-green-500' 
                : 'bg-blue-500'
            }`}
            style={{ 
              width: `${percentage}%`,
              animation: `growBar 1s ease-out ${index * 100}ms both`
            }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${shellCardClassName} ${className}`}>
      {/* Header */}
      <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50/80 to-transparent p-4 md:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center space-x-2 text-lg font-semibold text-slate-900">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span className="break-words">{session.question}</span>
            </h3>
            {session.description && (
              <p className="mt-1 text-sm text-slate-600 break-words">{session.description}</p>
            )}
          </div>
          
          <div className="flex w-full items-center justify-start md:w-auto md:justify-end">
            {/* Estado de la votación */}
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              isSessionActive 
                ? 'bg-green-100 text-green-800' 
                : isSessionEnded
                ? 'bg-blue-100 text-blue-800'
                : 'bg-slate-100 text-slate-800'
            }`}>
              <span className="inline-flex items-center gap-1.5">
                {isSessionActive ? (
                  <Circle className="h-3.5 w-3.5 text-green-700" aria-hidden="true" />
                ) : isSessionEnded ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />
                ) : (
                  <Clock className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
                )}
                <span>{isSessionActive ? 'En curso' : isSessionEnded ? 'Finalizada' : session.status}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Votación Activa: Solo mostrar contador de participación */}
      {isSessionActive && (
        <div className="p-6">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-6 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold text-slate-900">Votación en curso</h3>
            <p className="mb-4 text-slate-600">Los resultados se mostrarán cuando finalice la votación</p>
            
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-3xl font-bold text-blue-600">
                  {liveStats?.totalVotes ?? results?.totalVotes ?? 0}
                </div>
                <div className="text-sm text-slate-600">Votos emitidos</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-3xl font-bold text-green-600">
                  {(liveStats?.participationRate ?? results?.participationRate)
                    ? `${Math.round(liveStats?.participationRate ?? results?.participationRate ?? 0)}%`
                    : '0%'}
                </div>
                <div className="text-sm text-slate-600">Participación</div>
              </div>
            </div>

            {session.endsAt && (
              <div className="mt-4 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" aria-hidden="true" />
                  <span>Finaliza: {new Date(session.endsAt).toLocaleString('es-ES')}</span>
                </span>
              </div>
            )}
          </div>

          {/* Información adicional */}
          <div className="mt-4 space-y-1 text-xs text-slate-500">
            <p className="inline-flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <span>Total de opciones: {session.options.length}</span>
            </p>
            {session.isAnonymous && (
              <p className="inline-flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-500" aria-hidden="true" />
                <span>Votación anónima</span>
              </p>
            )}
            {session.allowAbstention && (
              <p className="inline-flex items-center gap-2">
                <MinusCircle className="h-4 w-4 text-slate-500" aria-hidden="true" />
                <span>Se permite abstención</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Votación Finalizada: Mostrar resultados completos */}
      {isSessionEnded && (
        <>
          {/* Estadísticas */}
          <div className="border-b border-slate-200 bg-slate-50 p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {results?.totalVotes || 0}
                </div>
                <div className="text-sm text-slate-600">Total de votos</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {results?.participationRate ? `${Math.round(results.participationRate)}%` : '0%'}
                </div>
                <div className="text-sm text-slate-600">Participación</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {session.options.length}
                </div>
                <div className="text-sm text-slate-600">Opciones</div>
              </div>
              <div>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-600 break-all leading-tight">
                  {formatCoefficient(results?.votedCoefficient)}
                </div>
                <div className="text-sm text-slate-600">Coef. emitido</div>
              </div>
            </div>
          </div>

          {/* Resultados por opción */}
          <div className="p-4">
            <div className="space-y-3">
              {session.options.map((option, index) => (
                <OptionResult key={option.id} optionText={option.text} index={index} />
              ))}
            </div>

            {/* Mensaje de resultado final */}
            {winner && (
              <div className="mt-4 rounded-xl border border-green-200 bg-green-100 p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-green-800">Votación finalizada</h4>
                    <p className="text-sm text-green-700">
                      Opción ganadora: <strong>{winner}</strong> con {formatCoefficient(getOptionResult(winner || '')?.coefficient)} coef.
                      ({getOptionPercentage(winner || '')}%)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Información adicional */}
            <div className="mt-4 space-y-1 text-xs text-slate-500">
              {session.isAnonymous && (
                <p className="inline-flex items-center gap-2">
                  <Lock className="h-4 w-4 text-slate-500" aria-hidden="true" />
                  <span>Votación anónima</span>
                </p>
              )}
              {session.allowAbstention && (
                <p className="inline-flex items-center gap-2">
                  <MinusCircle className="h-4 w-4 text-slate-500" aria-hidden="true" />
                  <span>Se permite abstención</span>
                </p>
              )}
              {session.endedAt && (
                <p className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" aria-hidden="true" />
                  <span>Finalizada: {new Date(session.endedAt).toLocaleString('es-ES')}</span>
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VotingResultsComponent;